import { FC } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { StoreName, stores } from '@namespace/helpers-store';

interface AppWrapperProps {
  initialPageConfig?: Record<string, unknown>;
}

type PartnerConfig = Record<string, unknown>;

// FLOW::HYDRATION
// init stores with data loaded from ssr since these stores are created before data is loaded
const initZustandStoresFromSsr = () => {
  const hydrateState = (hydrationState: Record<StoreName, unknown>) => {
    if (!hydrationState) {
      return;
    }

    for (const [storeName, state] of Object.entries(hydrationState) as [
      StoreName,
      unknown
    ][]) {
      if (state != null) {
        const useBoundStore = stores[storeName];

        // merge hydrated state with default state already in the store
        useBoundStore?.setInitialState(state);
      }
    }
  };

  hydrateState(window.initialStoresState);
  hydrateState(window.translations);
};

// FLOW::CLIENT + HYDRATION
const initZustandStores = (partnerConfig: PartnerConfig = {}) => {
  for (const store of Object.values(stores)) {
    // we don't bother splitting props for each store - their `init` functions will figure it out
    store.initialize?.(partnerConfig);
  }
};

const container = document.getElementById('root') as HTMLElement;

export const loadApp = (
  AppWrapper: FC<AppWrapperProps>,
  partnerConfig?: PartnerConfig
) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(
      'Dev Server Proxy > HTTP:',
      process.env.PROXY_TARGET,
      'WS:',
      process.env.PROXY_TARGET_SOCKET
    );
  }

  const startReact = () => {
    const root = createRoot(container);

    root.render(<AppWrapper initialPageConfig={window.pagesBatch?.configs} />);
  };

  const startHydrateReact = () => {
    initZustandStoresFromSsr();

    hydrateRoot(
      container,
      <AppWrapper initialPageConfig={window.pagesBatch?.configs} />
    );
  };

  initZustandStores(partnerConfig);

  if (window.IS_SSR) {
    const scriptElement = document.getElementById('ssr-page-data');

    if (window.ssrDataLoaded && window.siteSettings) {
      startHydrateReact();
    } else if (!window.ssrDataLoaded && scriptElement) {
      scriptElement.onload = () => {
        startHydrateReact();
      };
      scriptElement.onerror = () => {
        startReact();
      };
    } else {
      startReact();
    }
  } else {
    startReact();
  }
};
